/**
 * CustomerFormDialog - Modal para criação e edição de cliente
 *
 * Formulário com campos condicionais baseados no tipo (PF/PJ),
 * busca automática de endereço via CEP (ViaCEP), criação inline do contato
 * principal e detecção de duplicidade em tempo real.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-28 v3.3.0 - Herança do vendedor responsável: `prefillContact`
 *            passa a aceitar `seller_id` e o formulário (modo criação) já abre
 *            com o vendedor do contato de origem selecionado. Backend também
 *            herda (create_crm_customer v1.1.0) como rede de segurança.
 *   RELEASE: 2026-08-23 v3.2.1 - Correção: o atalho "Automático" sem delegação não fecha
 *            mais o formulário (o Dialog apenas fica oculto enquanto o wizard está aberto).
 *            Fechar desmontava o componente em pais condicionais (Criar rápido), matando o
 *            estado e o próprio wizard interno — nada acontecia ao clicar.
 *   RELEASE: 2026-08-23 v3.2.0 - Atalho "Automático" no rodapé (modo criação):
 *            abre o wizard de cadastro automático via integração (ERP). Só é
 *            renderizado quando o tenant possui conexão ativa com a capacidade
 *            `customer.lookup_document` (mesma regra do NewCustomerButton) e
 *            nunca no modo edição nem com `prefillContact`. Quando o pai
 *            informa `onAutomatic`, delega (reaproveita o wizard já montado);
 *            caso contrário, monta o wizard internamente.
 *   RELEASE: 2026-08-19 v3.1.0 - Suporte a "Transformar contato em cliente":
 *            quando `prefillContact` é informado, o contato de origem fica
 *            TRAVADO (campos somente leitura + chip identificando o contato) e
 *            o cliente é criado direto com esse `main_contact_id` (sem passar
 *            por find_or_create_contact_for_customer, evitando criar contato
 *            duplicado se o usuário alterar telefone/e-mail). Tratamento
 *            amigável do erro de duplicidade (contato já é cliente).
 *   RELEASE: 2026-08-09 v3.0.0 - Padronização visual com o modal de veículos
 *            (VehicleFormDialog): cabeçalho premium fixo, corpo rolável em
 *            ScrollArea, rodapé fixo, seções via FormSection/FieldLabel,
 *            largura max-w-5xl e fullscreen no mobile. Nenhuma alteração de
 *            regra de negócio, RPCs ou props públicas.
 *   RELEASE: 2026-03-15 v2.0.0 - Criação inline de contato + detecção de duplicidade
 */
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  User, 
  UserCog,
  Building2, 
  Layers,
  MapPin, 
  Search,
  Loader2,
  StickyNote,
  Calendar,
  UserPlus,
  AlertTriangle,
  Mail,
  Phone as PhoneIcon,
  // v3.2.0: ícone do atalho de cadastro automático (integração ERP)
  Zap,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FormSection, FieldLabel } from '@/components/common/FormSection';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { ContactSelector } from '@/components/crm/ContactSelector';
import { PhoneInput } from '@/components/ui/phone-input';
import { 
  useCrmCustomers, 
  CrmCustomer, 
  CustomerType,
  CustomerLifecycleStage,
  UpdateCustomerInput,
  CreateCustomerInput
} from '@/hooks/useCrmCustomers';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { applyCPFMask, applyCNPJMask, applyDateMask } from '@/lib/input-masks';
import { validateCPF, validateCNPJ } from '@/lib/input-validators';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
// v3.2.0: atalho de cadastro automático via integração (ERP)
import { useDocumentLookupConnections } from '@/hooks/integrations/useCustomerDocumentLookup';
import { CustomerAutoImportDialog } from '@/components/crm/customers/CustomerAutoImportDialog';

// ============================================
// TIPOS PARA DUPLICATAS
// ============================================

/** Resultado da busca de duplicatas */
interface DuplicateCustomer {
  id: string;
  customer_type: string;
  nome: string | null;
  razao_social: string | null;
  nome_fantasia: string | null;
  cpf: string | null;
  cnpj: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  match_field: string | null;
  /** ID do contato principal (necessário para buscar negócios vinculados) */
  main_contact_id: string | null;
}

// ============================================
// FUNÇÕES DE CONVERSÃO DE DATA
// ============================================

/**
 * Converte data do formato brasileiro (DD/MM/YYYY) para ISO (YYYY-MM-DD)
 * Necessário porque o PostgreSQL espera formato ISO
 */
function convertBrazilianDateToISO(date: string | null | undefined): string | null {
  if (!date) return null;
  const cleanDate = date.replace(/\D/g, '');
  if (cleanDate.length !== 8) return null;
  const day = cleanDate.slice(0, 2);
  const month = cleanDate.slice(2, 4);
  const year = cleanDate.slice(4, 8);
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);
  if (dayNum < 1 || dayNum > 31) return null;
  if (monthNum < 1 || monthNum > 12) return null;
  if (yearNum < 1900 || yearNum > 2100) return null;
  return `${year}-${month}-${day}`;
}

/**
 * Converte data do formato ISO (YYYY-MM-DD) para brasileiro (DD/MM/YYYY)
 */
function convertISOToBrazilianDate(date: string | null | undefined): string | null {
  if (!date) return null;
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

// ============================================
// SCHEMA DE VALIDAÇÃO
// ============================================
const customerFormSchema = z.object({
  // Campo para criação - contato principal (modo edição mantém o existente)
  main_contact_id: z.string().optional().nullable(),
  
  // Campos inline para criação de contato (modo criação)
  contact_name: z.string().optional().nullable(),
  contact_phone: z.string().optional().nullable(),
  contact_email: z.string().optional().nullable(),
  
  customer_type: z.enum(['pf', 'pj']),
  
  // Campos PF
  nome: z.string().optional().nullable(),
  cpf: z.string().optional().nullable(),
  data_nascimento: z.string().optional().nullable(),
  
  // Campos PJ
  razao_social: z.string().optional().nullable(),
  nome_fantasia: z.string().optional().nullable(),
  cnpj: z.string().optional().nullable(),
  
  // Endereço
  cep: z.string().optional().nullable(),
  logradouro: z.string().optional().nullable(),
  numero: z.string().optional().nullable(),
  complemento: z.string().optional().nullable(),
  bairro: z.string().optional().nullable(),
  cidade: z.string().optional().nullable(),
  estado: z.string().optional().nullable(),
  
  // Outros
  seller_id: z.string().optional().nullable(),
  lifecycle_stage: z.enum(['active', 'inactive', 'churned']).optional(),
  notes: z.string().optional().nullable(),
}).superRefine((data, ctx) => {
  // Validação condicional: nome obrigatório para PF, razão social obrigatória para PJ
  if (data.customer_type === 'pf' && !data.nome?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['nome'],
      message: 'Nome completo é obrigatório',
    });
  }
  if (data.customer_type === 'pj' && !data.razao_social?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['razao_social'],
      message: 'Razão social é obrigatória',
    });
  }
  // Validação: CPF obrigatório para Pessoa Física
  if (data.customer_type === 'pf' && (!data.cpf || data.cpf.replace(/\D/g, '').length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['cpf'],
      message: 'CPF é obrigatório para Pessoa Física',
    });
  }
  // Validação: CNPJ obrigatório para Pessoa Jurídica
  if (data.customer_type === 'pj' && (!data.cnpj || data.cnpj.replace(/\D/g, '').length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['cnpj'],
      message: 'CNPJ é obrigatório para Pessoa Jurídica',
    });
  }
});

type CustomerFormValues = z.infer<typeof customerFormSchema>;

// ============================================
// PROPS
// ============================================
interface CustomerFormDialogProps {
  /** Cliente a ser editado (null para modo criação) */
  customer: CrmCustomer | null;
  /** Estado de abertura do dialog */
  open: boolean;
  /** Callback para alteração do estado */
  onOpenChange: (open: boolean) => void;
  /** Callback de sucesso — recebe dados do cliente recém-criado (modo criação) */
  onSuccess?: (newCustomer?: { id: string; nome: string; customer_type: string; main_contact_id: string; cpf?: string | null; cnpj?: string | null; contact_phone?: string | null; contact_email?: string | null }) => void;
  /** Callback para abrir edição de cliente existente (quando duplicata é selecionada) */
  onEditExistingCustomer?: (customerId: string) => void;
  /** 
   * Callback para selecionar cliente existente (duplicata) sem abrir edição.
   * Usado quando o modal é aberto a partir de outro contexto (ex: Nova Cobrança)
   * para pré-selecionar o cliente automaticamente.
   * Tem prioridade sobre onEditExistingCustomer quando definido.
   */
  onSelectExistingCustomer?: (customer: {
    id: string;
    nome: string | null;
    customer_type: string;
    cpf: string | null;
    cnpj: string | null;
    contact_phone: string | null;
    contact_email: string | null;
    /** ID do contato principal (para buscar negócios) */
    main_contact_id: string | null;
  }) => void;
  /**
   * v2.1.0: Dados do contato para pré-preenchimento no modo criação
   * Usado quando o contato do deal precisa ser transformado em cliente
   */
  prefillContact?: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    /** v3.3.0: vendedor responsável do contato — herdado pelo cliente */
    seller_id?: string | null;
  } | null;
  /**
   * v3.2.0: quando informado, o botão "Automático" do rodapé fecha este modal e
   * delega a abertura do wizard ao componente pai (reaproveita o wizard já
   * montado na tela). Sem esta prop, o wizard é montado internamente.
   */
  onAutomatic?: () => void;
}

// ============================================
// COMPONENTE
// ============================================
export function CustomerFormDialog({
  customer,
  open,
  onOpenChange,
  onSuccess,
  onEditExistingCustomer,
  onSelectExistingCustomer,
  prefillContact,
  onAutomatic,
}: CustomerFormDialogProps) {
  const { createCustomer, updateCustomer, isCreating, isUpdating } = useCrmCustomers();
  const { currentTenant } = useAuth();
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  
  // Estado para detecção de duplicidade
  const [duplicates, setDuplicates] = useState<DuplicateCustomer[]>([]);
  const [isSearchingDuplicates, setIsSearchingDuplicates] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Determina se está em modo criação
  const isCreateMode = customer === null;
  const isSubmitting = isCreating || isUpdating;
  // v3.0.0: modal fullscreen no mobile (paridade com VehicleFormDialog)
  const isMobile = useIsMobile();

  // ------------------------------------------------------------------
  // v3.2.0: atalho de cadastro automático (integração ERP)
  // Mesma fonte de verdade do NewCustomerButton (query cacheada, sem custo extra).
  // Só habilitado no modo criação e fora do fluxo "transformar contato em cliente".
  // ------------------------------------------------------------------
  const canUseAutomatic = isCreateMode && !prefillContact;
  const { isAvailable: hasLookupIntegration } = useDocumentLookupConnections(
    open && canUseAutomatic,
  );
  const showAutomaticShortcut = canUseAutomatic && hasLookupIntegration;
  /** Wizard interno (usado apenas quando o pai não fornece onAutomatic) */
  const [internalAutoImportOpen, setInternalAutoImportOpen] = useState(false);

  /**
   * v3.2.1 — Abre o wizard automático.
   * Quando o pai delega (`onAutomatic`), fecha este modal e deixa o pai abrir o
   * wizard. Sem delegação, NÃO fechamos o formulário: apenas ocultamos o Dialog
   * (ver `open={open && !internalAutoImportOpen}`), pois fechar desmontaria este
   * componente em pais condicionais (ex.: Criar rápido) e o wizard nunca abriria.
   */
  const handleOpenAutomatic = useCallback(() => {
    if (onAutomatic) {
      onOpenChange(false);
      onAutomatic();
      return;
    }
    setInternalAutoImportOpen(true);
  }, [onAutomatic, onOpenChange]);



  // Formulário com react-hook-form
  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      main_contact_id: null,
      contact_name: null,
      contact_phone: null,
      contact_email: null,
      customer_type: 'pf',
      lifecycle_stage: 'active',
    },
  });

  // Observa tipo de cliente para renderização condicional
  const customerType = form.watch('customer_type');
  
  // Observa campos para detecção de duplicidade
  const watchedPhone = form.watch('contact_phone');
  const watchedEmail = form.watch('contact_email');
  const watchedCpf = form.watch('cpf');
  const watchedCnpj = form.watch('cnpj');

  // Preenche formulário quando cliente é carregado (modo edição)
  useEffect(() => {
    if (customer) {
      form.reset({
        main_contact_id: customer.main_contact_id,
        contact_name: null,
        contact_phone: null,
        contact_email: null,
        customer_type: customer.customer_type,
        nome: customer.nome,
        cpf: customer.cpf,
        data_nascimento: convertISOToBrazilianDate(customer.data_nascimento),
        razao_social: customer.razao_social,
        nome_fantasia: customer.nome_fantasia,
        cnpj: customer.cnpj,
        cep: customer.cep,
        logradouro: customer.logradouro,
        numero: customer.numero,
        complemento: customer.complemento,
        bairro: customer.bairro,
        cidade: customer.cidade,
        estado: customer.estado,
        seller_id: customer.seller_id,
        lifecycle_stage: 'active',
        notes: customer.notes,
      });
    } else {
      // Reset para valores padrão no modo criação
      // v2.1.0: Se prefillContact fornecido, pré-preenche dados do contato
      // v3.3.0: herda o vendedor responsável do contato de origem
      form.reset({
        main_contact_id: prefillContact?.id || null,
        contact_name: prefillContact?.name || null,
        contact_phone: prefillContact?.phone || null,
        contact_email: prefillContact?.email || null,
        seller_id: prefillContact?.seller_id || null,
        customer_type: 'pf',
        lifecycle_stage: 'active',
      });
      setDuplicates([]);
    }
  }, [customer, form, open]);

  // ============================================
  // DETECÇÃO DE DUPLICIDADE EM TEMPO REAL
  // ============================================
  const searchDuplicates = useCallback(async (
    phone: string | null | undefined,
    email: string | null | undefined,
    cpf: string | null | undefined,
    cnpj: string | null | undefined,
  ) => {
    if (!currentTenant?.id) return;
    
    // Limpa dígitos para verificar comprimento mínimo
    const cleanPhone = phone?.replace(/\D/g, '') || '';
    const cleanEmail = email?.trim() || '';
    const cleanCpf = cpf?.replace(/\D/g, '') || '';
    const cleanCnpj = cnpj?.replace(/\D/g, '') || '';
    
    // Só busca se tiver pelo menos um critério com comprimento suficiente
    const hasValidPhone = cleanPhone.length >= 10;
    const hasValidEmail = cleanEmail.includes('@') && cleanEmail.length >= 5;
    const hasValidCpf = cleanCpf.length >= 11;
    const hasValidCnpj = cleanCnpj.length >= 14;
    
    if (!hasValidPhone && !hasValidEmail && !hasValidCpf && !hasValidCnpj) {
      setDuplicates([]);
      return;
    }

    setIsSearchingDuplicates(true);
    try {
      const { data, error } = await (supabase.rpc as any)('search_duplicate_customers', {
        p_phone: hasValidPhone ? phone : null,
        p_email: hasValidEmail ? email : null,
        p_cpf: hasValidCpf ? cpf : null,
        p_cnpj: hasValidCnpj ? cnpj : null,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('[CustomerFormDialog] Erro ao buscar duplicatas:', error.message);
        setDuplicates([]);
        return;
      }

      setDuplicates(data || []);
    } catch (err) {
      logger.error('[CustomerFormDialog] Erro inesperado ao buscar duplicatas:', err);
      setDuplicates([]);
    } finally {
      setIsSearchingDuplicates(false);
    }
  }, [currentTenant?.id]);

  // Debounce da busca de duplicatas (apenas no modo criação)
  useEffect(() => {
    if (!isCreateMode || !open) return;
    
    if (debounceTimer) clearTimeout(debounceTimer);
    
    const timer = setTimeout(() => {
      searchDuplicates(watchedPhone, watchedEmail, watchedCpf, watchedCnpj);
    }, 600);
    
    setDebounceTimer(timer);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedPhone, watchedEmail, watchedCpf, watchedCnpj, isCreateMode, open]);

  // ============================================
  // HANDLER PARA SELECIONAR DUPLICATA
  // ============================================
  const handleSelectDuplicate = (duplicateId: string) => {
    // Busca os dados da duplicata selecionada
    const selectedDup = duplicates.find(d => d.id === duplicateId);

    // Prioridade 1: callback de seleção direta (ex: modal de Nova Cobrança)
    if (onSelectExistingCustomer && selectedDup) {
      logger.info('[CustomerFormDialog] Duplicata selecionada via onSelectExistingCustomer:', duplicateId);
      onOpenChange(false);
      onSelectExistingCustomer({
        id: selectedDup.id,
        nome: selectedDup.nome || selectedDup.razao_social || selectedDup.nome_fantasia || null,
        customer_type: selectedDup.customer_type,
        cpf: selectedDup.cpf,
        cnpj: selectedDup.cnpj,
        contact_phone: selectedDup.contact_phone,
        contact_email: selectedDup.contact_email,
        main_contact_id: selectedDup.main_contact_id,
      });
      return;
    }

    // Prioridade 2: abrir edição do cliente existente
    onOpenChange(false);
    if (onEditExistingCustomer) {
      // Pequeno delay para o modal fechar antes de abrir outro
      setTimeout(() => {
        onEditExistingCustomer(duplicateId);
      }, 200);
    } else {
      toast.info('Cliente já existe. Selecione-o na listagem para editar.');
    }
  };

  // ============================================
  // BUSCA CEP VIA VIACEP
  // ============================================
  const handleCepSearch = async () => {
    const cep = form.getValues('cep')?.replace(/\D/g, '');
    
    if (!cep || cep.length !== 8) {
      toast.error('CEP inválido. Digite 8 dígitos.');
      return;
    }

    setIsLoadingCep(true);
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado.');
        return;
      }
      
      form.setValue('logradouro', data.logradouro || '');
      form.setValue('bairro', data.bairro || '');
      form.setValue('cidade', data.localidade || '');
      form.setValue('estado', data.uf || '');
      
      const numeroInput = document.querySelector('input[name="numero"]');
      if (numeroInput) {
        (numeroInput as HTMLInputElement).focus();
      }
      
      toast.success('Endereço preenchido!');
    } catch (error) {
      toast.error('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setIsLoadingCep(false);
    }
  };

  // ============================================
  // SUBMIT
  // ============================================
  const onSubmit = async (values: CustomerFormValues) => {
    // Validação de CPF/CNPJ
    if (values.customer_type === 'pf' && values.cpf) {
      const cpfResult = validateCPF(values.cpf);
      if (!cpfResult.isValid) {
        form.setError('cpf', { message: cpfResult.error });
        return;
      }
    }
    
    if (values.customer_type === 'pj' && values.cnpj) {
      const cnpjResult = validateCNPJ(values.cnpj);
      if (!cnpjResult.isValid) {
        form.setError('cnpj', { message: cnpjResult.error });
        return;
      }
    }

    if (isCreateMode) {
      // =========================================================
      // MODO CRIAÇÃO - Cria contato automaticamente via RPC
      // =========================================================
      const contactName = values.contact_name?.trim();
      const contactPhone = values.contact_phone?.trim();
      const contactEmail = values.contact_email?.trim();

      // Validação: nome do contato é obrigatório
      if (!contactName) {
        form.setError('contact_name', { message: 'Nome do contato é obrigatório' });
        return;
      }

      // Validação: pelo menos telefone ou email
      if (!contactPhone && !contactEmail) {
        toast.error('Informe pelo menos um telefone ou email para o contato.');
        return;
      }

      try {
        // Passo 1: Resolve o contato principal
        // v3.1.0: no fluxo "Transformar contato em cliente" o contato de origem
        // é imutável — usamos o id recebido, sem criar/procurar outro contato.
        let contactId: string | null = prefillContact?.id ?? null;

        if (!contactId) {
          const { data: resolvedId, error: contactError } = await (supabase.rpc as any)(
            'find_or_create_contact_for_customer',
            {
              p_name: contactName,
              p_phone: contactPhone || null,
              p_email: contactEmail || null,
              p_tenant_hint: currentTenant?.id,
            }
          );

          if (contactError) {
            logger.error('[CustomerFormDialog] Erro ao criar contato:', contactError.message);
            toast.error(`Erro ao criar contato: ${contactError.message}`);
            return;
          }

          contactId = resolvedId ?? null;
        }

        if (!contactId) {
          toast.error('Erro inesperado ao criar contato.');
          return;
        }


        // Passo 2: Cria cliente com o main_contact_id obtido
        const input: CreateCustomerInput = {
          main_contact_id: contactId,
          customer_type: values.customer_type,
          nome: values.nome,
          cpf: values.cpf,
          data_nascimento: convertBrazilianDateToISO(values.data_nascimento),
          razao_social: values.razao_social,
          nome_fantasia: values.nome_fantasia,
          cnpj: values.cnpj,
          cep: values.cep,
          logradouro: values.logradouro,
          numero: values.numero,
          complemento: values.complemento,
          bairro: values.bairro,
          cidade: values.cidade,
          estado: values.estado,
          seller_id: values.seller_id,
          lifecycle_stage: 'active',
          notes: values.notes,
        };

        createCustomer.mutate(input, {
          onSuccess: (result) => {
            // Monta objeto com dados do cliente recém-criado para pré-seleção
            const newCustomer = {
              id: result.id,
              nome: values.nome || values.razao_social || '',
              customer_type: values.customer_type,
              main_contact_id: contactId,
              cpf: values.cpf || null,
              cnpj: values.cnpj || null,
              contact_phone: contactPhone || null,
              contact_email: contactEmail || null,
            };
            onOpenChange(false);
            onSuccess?.(newCustomer);
          },
        });
      } catch (err) {
        logger.error('[CustomerFormDialog] Erro inesperado na criação:', err);
        toast.error('Erro inesperado ao criar cliente.');
      }
    } else {
      // MODO EDIÇÃO
      const input: UpdateCustomerInput = {
        id: customer!.id,
        customer_type: values.customer_type,
        nome: values.nome,
        cpf: values.cpf,
        data_nascimento: convertBrazilianDateToISO(values.data_nascimento),
        razao_social: values.razao_social,
        nome_fantasia: values.nome_fantasia,
        cnpj: values.cnpj,
        cep: values.cep,
        logradouro: values.logradouro,
        numero: values.numero,
        complemento: values.complemento,
        bairro: values.bairro,
        cidade: values.cidade,
        estado: values.estado,
        seller_id: values.seller_id,
        lifecycle_stage: 'active',
        notes: values.notes,
      };

      updateCustomer.mutate(input, {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      });
    }
  };

  // ============================================
  // MÁSCARAS
  // ============================================
  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyCPFMask(e.target.value);
    form.setValue('cpf', masked);
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyCNPJMask(e.target.value);
    form.setValue('cnpj', masked);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    form.setValue('data_nascimento', masked);
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const limited = value.slice(0, 8);
    let formatted = limited;
    if (limited.length > 5) {
      formatted = `${limited.slice(0, 5)}-${limited.slice(5, 8)}`;
    }
    form.setValue('cep', formatted);
  };

  // ============================================
  // HELPER: Nome de exibição da duplicata
  // ============================================
  const getDuplicateDisplayName = (dup: DuplicateCustomer) => {
    return dup.nome_fantasia || dup.razao_social || dup.nome || 'Sem nome';
  };

  // Helper: Label do campo que causou o match
  const getMatchLabel = (field: string | null) => {
    switch (field) {
      case 'cpf': return 'CPF';
      case 'cnpj': return 'CNPJ';
      case 'phone': return 'Telefone';
      case 'email': return 'E-mail';
      default: return 'Dado';
    }
  };

  return (
    <>
    {/* v3.2.1: oculto (mas montado) enquanto o wizard automático interno está aberto */}
    <Dialog open={open && !internalAutoImportOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex flex-col gap-0 overflow-hidden p-0',
          isMobile
            ? 'h-[100dvh] w-screen max-w-full rounded-none'
            : 'max-h-[90vh] max-w-5xl',
        )}
      >
        {/* Cabeçalho premium (padrão VehicleFormDialog/DealFormDialog) */}
        <DialogHeader className="shrink-0 border-b p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5">
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              ) : isCreateMode ? (
                <UserPlus className="h-5 w-5 text-primary" />
              ) : customerType === 'pf' ? (
                <User className="h-5 w-5 text-primary" />
              ) : (
                <Building2 className="h-5 w-5 text-primary" />
              )}
            </div>
            <div className="text-left">
              <DialogTitle className="text-lg">
                {isCreateMode ? 'Novo Cliente' : 'Editar Cliente'}
              </DialogTitle>
              <DialogDescription className="text-sm">
                {isCreateMode
                  ? 'Preencha os dados para criar um novo cliente. O contato principal será criado automaticamente.'
                  : 'Atualize as informações do cliente abaixo.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Form {...form}>
          {/* O form envolve corpo rolável + rodapé fixo para o submit continuar funcionando */}
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <ScrollArea className="flex-1 overflow-auto">
              <div className="space-y-6 p-6">
                {/* ================= Contato principal (somente criação) ================= */}
                {isCreateMode && (
                  <>
                    <FormSection icon={User} title="Contato Principal">
                      {/* v3.1.0: contato de origem travado (conversão contato → cliente) */}
                      {prefillContact && (
                        <div className="mb-3 flex items-center gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2">
                          <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">
                            Convertendo o contato{' '}
                            <span className="font-medium text-foreground">
                              {prefillContact.name || 'sem nome'}
                            </span>{' '}
                            em cliente. Os dados do contato permanecem inalterados.
                          </p>
                        </div>
                      )}
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {/* Nome do contato */}
                        <FormField
                          control={form.control}
                          name="contact_name"
                          render={({ field }) => (
                            <FormItem className="space-y-1.5 lg:col-span-1 sm:col-span-2">
                              <FieldLabel required>Nome</FieldLabel>
                              <FormControl>
                                <Input
                                  placeholder="Nome do contato principal"
                                  className="h-9 border-border/50"
                                  {...field}
                                  value={field.value || ''}
                                  disabled={!!prefillContact}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Telefone */}
                        <FormField
                          control={form.control}
                          name="contact_phone"
                          render={({ field }) => (
                            <FormItem className="space-y-1.5">
                              <FieldLabel>
                                <PhoneIcon className="h-3 w-3" />
                                Telefone
                              </FieldLabel>
                              <FormControl>
                                <PhoneInput
                                  value={field.value || ''}
                                  onChange={(val) => field.onChange(val)}
                                  placeholder="(00) 00000-0000"
                                  disabled={!!prefillContact}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* E-mail */}
                        <FormField
                          control={form.control}
                          name="contact_email"
                          render={({ field }) => (
                            <FormItem className="space-y-1.5">
                              <FieldLabel>
                                <Mail className="h-3 w-3" />
                                E-mail
                              </FieldLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="email@exemplo.com"
                                  className="h-9 border-border/50"
                                  {...field}
                                  value={field.value || ''}
                                  disabled={!!prefillContact}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Alerta de duplicidade */}
                      {duplicates.length > 0 && (
                        <Alert
                          variant="destructive"
                          className="border-destructive/50 bg-destructive/10 [&>svg]:text-destructive"
                        >
                          <AlertTriangle className="h-4 w-4" />
                          <AlertTitle>Cliente já cadastrado!</AlertTitle>
                          <AlertDescription className="space-y-2">
                            <p className="text-sm">
                              Encontramos{' '}
                              {duplicates.length === 1
                                ? 'um cliente'
                                : `${duplicates.length} clientes`}{' '}
                              com dados semelhantes. Clique para editar:
                            </p>
                            <div className="space-y-1.5">
                              {duplicates.map((dup) => (
                                <button
                                  key={dup.id}
                                  type="button"
                                  onClick={() => handleSelectDuplicate(dup.id)}
                                  className="flex w-full items-center gap-2 rounded-md border border-border bg-background p-2 text-left transition-colors hover:bg-accent"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className="truncate text-sm font-medium text-foreground">
                                        {getDuplicateDisplayName(dup)}
                                      </span>
                                      <Badge variant="outline" className="h-4 shrink-0 px-1 py-0 text-[9px]">
                                        {dup.customer_type === 'pj' ? 'PJ' : 'PF'}
                                      </Badge>
                                      <Badge variant="secondary" className="h-4 shrink-0 px-1 py-0 text-[9px]">
                                        {getMatchLabel(dup.match_field)}
                                      </Badge>
                                    </div>
                                    <span className="block truncate text-xs text-muted-foreground">
                                      {[
                                        dup.contact_phone ? formatPhoneForDisplay(dup.contact_phone) : null,
                                        dup.contact_email,
                                      ]
                                        .filter(Boolean)
                                        .join(' • ')}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Indicador de busca de duplicatas */}
                      {isSearchingDuplicates && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Verificando duplicidade...
                        </div>
                      )}
                    </FormSection>

                    <Separator />
                  </>
                )}

                {/* ================= Tipo de cliente ================= */}
                <FormSection icon={Layers} title="Tipo de Cliente">
                  <FormField
                    control={form.control}
                    name="customer_type"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <Tabs value={field.value} onValueChange={field.onChange} className="w-full">
                          <TabsList className="grid w-full grid-cols-2 sm:max-w-md">
                            <TabsTrigger value="pf" className="gap-2">
                              <User className="h-4 w-4" />
                              Pessoa Física
                            </TabsTrigger>
                            <TabsTrigger value="pj" className="gap-2">
                              <Building2 className="h-4 w-4" />
                              Pessoa Jurídica
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      </FormItem>
                    )}
                  />
                </FormSection>

                <Separator />

                {/* ================= Dados Pessoais (PF) ================= */}
                {customerType === 'pf' && (
                  <FormSection icon={User} title="Dados Pessoais">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <FormField
                        control={form.control}
                        name="nome"
                        render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <FieldLabel required>Nome Completo</FieldLabel>
                            <FormControl>
                              <Input
                                placeholder="Nome completo"
                                className="h-9 border-border/50"
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="cpf"
                        render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <FieldLabel required>CPF</FieldLabel>
                            <FormControl>
                              <Input
                                placeholder="000.000.000-00"
                                className="h-9 border-border/50"
                                {...field}
                                value={field.value || ''}
                                onChange={handleCpfChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="data_nascimento"
                        render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <FieldLabel>
                              <Calendar className="h-3 w-3" />
                              Data de Nascimento
                            </FieldLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  placeholder="DD/MM/AAAA"
                                  className="h-9 border-border/50 pr-9"
                                  {...field}
                                  value={field.value || ''}
                                  onChange={handleDateChange}
                                />
                                <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </FormSection>
                )}

                {/* ================= Dados da Empresa (PJ) ================= */}
                {customerType === 'pj' && (
                  <FormSection icon={Building2} title="Dados da Empresa">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <FormField
                        control={form.control}
                        name="razao_social"
                        render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <FieldLabel required>Razão Social</FieldLabel>
                            <FormControl>
                              <Input
                                placeholder="Razão social da empresa"
                                className="h-9 border-border/50"
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="nome_fantasia"
                        render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <FieldLabel>Nome Fantasia</FieldLabel>
                            <FormControl>
                              <Input
                                placeholder="Nome fantasia"
                                className="h-9 border-border/50"
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="cnpj"
                        render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <FieldLabel required>CNPJ</FieldLabel>
                            <FormControl>
                              <Input
                                placeholder="00.000.000/0000-00"
                                className="h-9 border-border/50"
                                {...field}
                                value={field.value || ''}
                                onChange={handleCnpjChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </FormSection>
                )}

                <Separator />

                {/* ================= Endereço ================= */}
                <FormSection icon={MapPin} title="Endereço" hint="(opcional)">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                    <FormField
                      control={form.control}
                      name="cep"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5 sm:col-span-2">
                          <FieldLabel>CEP</FieldLabel>
                          <FormControl>
                            <div className="flex gap-2">
                              <Input
                                placeholder="00000-000"
                                className="h-9 border-border/50"
                                {...field}
                                value={field.value || ''}
                                onChange={handleCepChange}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-9 shrink-0"
                                onClick={handleCepSearch}
                                disabled={isLoadingCep}
                              >
                                {isLoadingCep ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Search className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="logradouro"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5 sm:col-span-3">
                          <FieldLabel>Logradouro</FieldLabel>
                          <FormControl>
                            <Input
                              placeholder="Rua, Avenida..."
                              className="h-9 border-border/50"
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="numero"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5 sm:col-span-1">
                          <FieldLabel>Número</FieldLabel>
                          <FormControl>
                            <Input
                              placeholder="Nº"
                              className="h-9 border-border/50"
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="complemento"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5 sm:col-span-2">
                          <FieldLabel>Complemento</FieldLabel>
                          <FormControl>
                            <Input
                              placeholder="Apto, Sala..."
                              className="h-9 border-border/50"
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="bairro"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5 sm:col-span-2">
                          <FieldLabel>Bairro</FieldLabel>
                          <FormControl>
                            <Input
                              placeholder="Bairro"
                              className="h-9 border-border/50"
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cidade"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5 sm:col-span-1">
                          <FieldLabel>Cidade</FieldLabel>
                          <FormControl>
                            <Input
                              placeholder="Cidade"
                              className="h-9 border-border/50"
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="estado"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5 sm:col-span-1">
                          <FieldLabel>UF</FieldLabel>
                          <FormControl>
                            <Input
                              placeholder="UF"
                              maxLength={2}
                              className="h-9 border-border/50 uppercase"
                              {...field}
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </FormSection>

                <Separator />

                {/* ================= Atribuição ================= */}
                <FormSection icon={UserCog} title="Atribuição">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="seller_id"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5">
                          <FieldLabel>Vendedor Responsável</FieldLabel>
                          <FormControl>
                            <SellerSelector
                              value={field.value || undefined}
                              onSelect={(sellerId) => field.onChange(sellerId || null)}
                              placeholder="Selecione um vendedor"
                              allowClear
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {/* Campo "Status" removido: cliente é sempre Ativo */}

                  </div>
                </FormSection>

                <Separator />

                {/* ================= Observações ================= */}
                <FormSection icon={StickyNote} title="Observações" hint="(opcional)">
                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <FormControl>
                          <Textarea
                            placeholder="Observações sobre o cliente..."
                            className="resize-none border-border/50"
                            rows={3}
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormSection>
              </div>
            </ScrollArea>

            {/* Rodapé fixo (padrão VehicleFormDialog) */}
            <DialogFooter className="shrink-0 gap-2 border-t p-6 py-4 sm:justify-between">
              {/* v3.2.0: atalho automático — só existe com integração elegível */}
              {showAutomaticShortcut ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenAutomatic}
                  className="gap-1.5 text-muted-foreground hover:text-foreground"
                >
                  <Zap className="h-4 w-4 text-primary" />
                  Automático
                </Button>
              ) : (
                <span className="hidden sm:block" />
              )}

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isCreateMode ? 'Criar Cliente' : 'Salvar'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>

    {/* v3.2.0: wizard automático interno (apenas quando o pai não delega) */}
    {!onAutomatic && (
      <CustomerAutoImportDialog
        open={internalAutoImportOpen}
        onOpenChange={setInternalAutoImportOpen}
        onSuccess={() => {
          setInternalAutoImportOpen(false);
          onSuccess?.();
          onOpenChange(false);
        }}
      />
    )}
    </>
  );
}

